import React, { createContext, type ReactNode, useContext, useMemo } from 'react';

interface UnifiedAnalyticsConfig {
	cloudId?: string;
	crossAppInteractionSessionsEnabled?: boolean;
	hasDiscoverySection?: boolean;
	hasSwitcherAnalytics?: boolean;
	hasThirdPartySection?: boolean;
	productSwitcher?: string;
}

interface UnifiedAnalyticsContextProviderProps {
	children: ReactNode;
	cloudId?: string;
	crossAppInteractionSessionsEnabled?: boolean;
	hasDiscoverySection?: boolean;
	hasSwitcherAnalytics?: boolean;
	hasThirdPartySection?: boolean;
	productSwitcher?: string;
}

const UnifiedAnalyticsContext = createContext<UnifiedAnalyticsConfig | undefined>(undefined);

export const UnifiedAnalyticsContextProvider = ({
	crossAppInteractionSessionsEnabled = false,
	productSwitcher = 'appSwitcher',
	cloudId = 'unknown',
	hasSwitcherAnalytics = false,
	hasThirdPartySection = false,
	hasDiscoverySection = false,
	children,
}: UnifiedAnalyticsContextProviderProps): React.JSX.Element => {
	const contextValue = useMemo<UnifiedAnalyticsConfig>(
		() => ({
			crossAppInteractionSessionsEnabled,
			productSwitcher,
			cloudId,
			hasSwitcherAnalytics,
			hasThirdPartySection,
			hasDiscoverySection,
		}),
		[
			productSwitcher,
			crossAppInteractionSessionsEnabled,
			cloudId,
			hasSwitcherAnalytics,
			hasThirdPartySection,
			hasDiscoverySection,
		],
	);

	return (
		<UnifiedAnalyticsContext.Provider value={contextValue}>
			{children}
		</UnifiedAnalyticsContext.Provider>
	);
};

export const useUnifiedAnalyticsContext = (): UnifiedAnalyticsConfig => {
	const context = useContext(UnifiedAnalyticsContext);

	return (
		context ?? {
			crossAppInteractionSessionsEnabled: false,
			productSwitcher: 'appSwitcher',
			cloudId: 'unknown',
			hasSwitcherAnalytics: false,
			hasThirdPartySection: false,
			hasDiscoverySection: false,
		}
	);
};
